import React from 'react';
import { Address } from '../../../../../../../types/Common';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import { AddressInput, Box } from '@wegroup/design-system';
import { useFlowsRouteParams } from '../../../../hooks/useFlowsRouteParams';
import { useFlowsSession } from '../../../../api/getFlowsSessionById';
import { useTranslation } from 'react-i18next';

interface Props {
  question: FlowsQuestion;
}

/**
 * Simple address input component for use in table/modal contexts
 * This component renders a compact address input without the full screen layout
 */
const SimpleAddressInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const { t } = useTranslation();
  const {
    field: { value, onChange, onBlur },
    error,
    prefilled,
  } = useFlowInputComponent({ question });

  const { sessionId } = useFlowsRouteParams();
  const session = useFlowsSession(sessionId);

  const partyId = session?.data?.prospect?.id || '';
  const supportedCountries = question.rules.address?.allowed_countries;
  const defaultCountry = supportedCountries?.length
    ? supportedCountries[0]
    : '';

  const addressAnswer = value as Address | undefined;

  const handleChange = (address?: Address) => {
    if (!address) {
      onChange({ name: question.id, value: null });
      return;
    }

    onChange({
      name: question.id,
      value: {
        ...(value as Address),
        ...address,
        _local: { self: address?.self },
      },
    });
  };

  const handleBlur = () => {
    onBlur({ name: question.id, value });
  };

  return (
    <Box>
      <AddressInput
        name={question.id}
        value={addressAnswer}
        error={error && t('Please fill in a valid address')}
        onChange={handleChange}
        onBlur={handleBlur}
        country={addressAnswer?.country_code || defaultCountry || ''}
        isPrefilled={prefilled}
        isDisabled={question.rules.is_disabled}
        partyId={partyId}
        isLookupAllowed={question.rules.address?.autocomplete_enabled !== false}
        supportedCountries={supportedCountries}
      />
    </Box>
  );
};

export default SimpleAddressInputComponent;
